export interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

export const faqCategories = [
  'Getting Started',
  'Compliance & Regulations',
  'Technology & Integrations',
  'Pricing & ROI',
  'Customization & Control',
];

export const allFAQs: FAQItem[] = [
  {
    category: 'Getting Started',
    question: "Who is an AI voice caller built for?",
    answer: "Agents and teams with consistent lead flow who want faster follow-up, database reactivation, or help qualifying leads. We assess the fit around your volume and workflow."
  },
  {
    category: 'Getting Started',
    question: "How long does setup take?",
    answer: "Most builds take 2 to 3 weeks. We handle configuration and CRM integration, then test and refine the caller with you before launch."
  },
  {
    category: 'Getting Started',
    question: "What technical requirements do I need on my end?",
    answer: "Your CRM, lead sources, and platform accounts. We handle the technical setup and ongoing maintenance."
  },
  {
    category: 'Getting Started',
    question: "Can I get a demo before committing?",
    answer: "Yes. Hear a live conversation and see how qualification and booking work before making a decision."
  },
  {
    category: 'Getting Started',
    question: "What is speed-to-lead and why does it matter?",
    answer: "It is the time between a new inquiry and your first response. The system can call new leads within 30 seconds, subject to your configured calling windows."
  },
  {
    category: 'Getting Started',
    question: "What types of calls can the AI handle?",
    answer: "New-lead follow-up, database reactivation, inbound qualification, and appointment reminders. Each workflow is configured for your team."
  },
  {
    category: 'Compliance & Regulations',
    question: "Is this compliant with CASL for Canadian real estate?",
    answer: "Yes. The system only calls leads you have express or implied consent to contact under CASL (Canada's Anti-Spam Legislation). We configure call scheduling, consent logging, and opt-out handling to match CASL requirements. You maintain a full call log and consent record for every contact."
  },
  {
    category: 'Compliance & Regulations',
    question: "Does the AI comply with TCPA regulations in the United States?",
    answer: "Yes. The system is configured for full TCPA compliance, including honoring the National Do Not Call Registry, respecting calling hour restrictions, and maintaining consent records for every contact. Every call is logged with timestamp and outcome for compliance auditing."
  },
  {
    category: 'Compliance & Regulations',
    question: "Does the AI disclose that it is not a human?",
    answer: "This is configurable and depends on your jurisdiction. In most Canadian provinces, disclosure that the caller is an automated system is required upon request. We configure the AI to disclose its nature when directly asked and can set it to proactively disclose at the start of every call if your compliance requirements or preferences call for it."
  },
  {
    category: 'Compliance & Regulations',
    question: "What happens if a lead asks to be removed from calls?",
    answer: "The AI recognizes opt-out requests in real time, immediately ends the call, and marks the contact as do-not-call in your CRM. This happens automatically with no manual intervention needed and creates a compliant audit trail."
  },
  {
    category: 'Technology & Integrations',
    question: "What CRMs does the AI integrate with?",
    answer: "Follow Up Boss, BoldTrail, HubSpot, Real Geeks, Salesforce, Agent Locator, GoHighLevel, Lofty, and platforms with suitable API or Zapier connections. We confirm compatibility before building."
  },
  {
    category: 'Technology & Integrations',
    question: "How many calls can the AI make per day?",
    answer: "Capacity depends on your platform, lead volume, and calling windows. We configure concurrency and daily limits around your workflow."
  },
  {
    category: 'Technology & Integrations',
    question: "What happens to leads that do not answer?",
    answer: "The system follows your callback schedule, logs each attempt, and updates the next action in your CRM. Voicemail and SMS can be configured where appropriate."
  },
  {
    category: 'Technology & Integrations',
    question: "Isn't this the same as my CRM's built-in AI?",
    answer: "This is a custom voice caller that has two-way phone conversations, qualifies leads, and books appointments. It connects to your CRM and uses your scripts."
  },
  {
    category: 'Technology & Integrations',
    question: "Can the AI handle bilingual calls in French and English?",
    answer: "Yes. We can configure English and French conversations, language detection, and switching around your market."
  },
  {
    category: 'Pricing & ROI',
    question: "How much does an AI caller cost compared to hiring an ISA?",
    answer: "Your costs include a one-time setup fee, ongoing retainer, and usage on your platform accounts. We quote around your workflow so you can compare the total with your current follow-up costs."
  },
  {
    category: 'Pricing & ROI',
    question: "What kind of ROI can I expect?",
    answer: "Results depend on lead quality, volume, your market, and follow-up. Use the calculator to explore assumptions, then discuss your baseline with us."
  },
  {
    category: 'Pricing & ROI',
    question: "How is Point Break Systems priced?",
    answer: "One setup fee plus an ongoing support and maintenance retainer. Your system runs on your accounts. The quote depends on call volume, workflows, and integration complexity."
  },
  {
    category: 'Customization & Control',
    question: "Can I customize the AI's voice and scripts?",
    answer: "Yes. You choose the voice, tone, qualification questions, and call flow. We test the scripts with you and refine them from feedback."
  },
  {
    category: 'Customization & Control',
    question: "Do I own the system after it is built?",
    answer: "Yes. The infrastructure, scripts, and configuration live on your accounts. Your retainer covers support, maintenance, and refinement."
  },
  {
    category: 'Customization & Control',
    question: "What if the AI says something wrong during a call?",
    answer: "Review the call logs with us. We use the conversation to update scripts, instructions, and handling of similar situations."
  },
  {
    category: 'Customization & Control',
    question: "Can I use this for a business outside of real estate?",
    answer: "Yes. The same approach can support mortgage, insurance, home services, and other businesses that rely on lead follow-up."
  }
];

export const homepageFAQs: FAQItem[] = [
  ...allFAQs.slice(0, 10),
  {
    category: 'Getting Started',
    question: 'Is this right for my business?',
    answer: "This is built for agents and teams with consistent lead flow who want to invest in their follow-up. It is less suited to early-stage businesses with very low lead volume or no established lead source. We scope the fit with you before the build.",
  },
  {
    category: 'Technology & Integrations',
    question: 'Will this sound human?',
    answer: "The AI uses conversational scripts built for real estate, with natural pacing, qualifying questions, and objection handling. Hear it for yourself using the Try the AI link below the video.",
  },
  {
    category: 'Technology & Integrations',
    question: 'Does it work with my CRM?',
    answer: "We integrate with Follow Up Boss, BoldTrail, Real Geeks, GoHighLevel, Agent Locator, HubSpot, and platforms with a suitable API or Zapier connection. We handle the integration and confirm compatibility before building.",
  },
  {
    category: 'Customization & Control',
    question: "What if it doesn't work in my market?",
    answer: "Each build is configured for your niche, geography, lead type, and qualification criteria. We test with you and refine the scripts and follow-up based on real call results.",
  },
  {
    category: 'Pricing & ROI',
    question: 'How does the setup fee and retainer work?',
    answer: "The one-time setup fee covers your build, CRM integration, and training on your own accounts. The ongoing retainer covers support, maintenance, and refinement. You own and control the infrastructure, scripts, and call data. We quote based on your workflow and call volume.",
  },
];
